import {Fullstory, init, isInitialized} from '@fullstory/browser';
import {isLocalHost} from '../config';
import {isEnabled, unleash} from './unleash';

const ORG_ID = 'o-D9BV-eu1';
// OneTrust category that gates session capture (Performance/Analytics).
const CONSENT_CATEGORY = 'C0002';
// Unleash kill switch, checked only before starting capture. A running session
// is never stopped by the flag (toggle evaluation can flap); only consent is.
const FEATURE_FLAG = 'mp-fullstory';

let flagEnabled = false;
let capturing = false;

const hasConsent = () =>
  Boolean(
    (window as {OnetrustActiveGroups?: string}).OnetrustActiveGroups?.includes(
      CONSENT_CATEGORY,
    ),
  );

const debug = (...args: unknown[]) => {
  if (localStorage.getItem('fs_debug')) console.info('[fullstory]', ...args);
};

const sync = (source: string) => {
  const consent = hasConsent();
  debug(source, {consent, flagEnabled, capturing});

  if (!consent) {
    if (!capturing) return;
    capturing = false;
    if (isInitialized()) Fullstory('shutdown');
    return;
  }
  if (capturing || !flagEnabled) return;
  capturing = true;

  if (isInitialized()) {
    Fullstory('restart');
    return;
  }
  init({orgId: ORG_ID}, ({sessionUrl}) => {
    if (isLocalHost || localStorage.getItem('fs_debug')) {
      console.info(`FullStory session: ${sessionUrl}`);
    }
  });
  observeLifecycle();
};

// Logs every capture start/stop the SDK performs so gaps in a replay can be
// attributed (our gate, the SDK itself, or the page being unloaded).
const observeLifecycle = () => {
  Fullstory('observe', {
    type: 'start',
    callback: ({sessionUrl}) => debug('capture started', sessionUrl),
  });
  Fullstory('observe', {
    type: 'shutdown',
    callback: ({reason}) =>
      console.warn('[fullstory] capture stopped', {
        reason,
        consent: hasConsent(),
        flagEnabled,
        capturing,
        url: location.pathname,
      }),
  });
  Fullstory('observe', {
    type: 'internal/error',
    callback: ({message}) => console.warn('[fullstory] error', message),
  });
  Fullstory('observe', {
    type: 'internal/bundle',
    callback: ({sessionId, seq}) => trackSession(sessionId, `bundle #${seq}`),
  });
  window.addEventListener('pagehide', () =>
    debug('page unloading', {capturing, url: location.pathname}),
  );
};

// The SDK has no "session ended" event; a session is closed when the next
// bundle carries a different id. Persisted so reloads are compared too.
const SESSION_KEY = 'fs_last_session';
const trackSession = (sessionId: string, source: string) => {
  const previous = sessionStorage.getItem(SESSION_KEY);
  if (previous === sessionId) return;
  sessionStorage.setItem(SESSION_KEY, sessionId);
  if (!previous) return debug('session opened', sessionId);
  console.warn('[fullstory] session closed', {
    previous,
    next: sessionId,
    source,
    url: location.pathname,
  });
};

const applyFlag = () => {
  flagEnabled = isEnabled(FEATURE_FLAG);
  sync('unleash');
};

export const initFullStory = () => {
  window.addEventListener('OneTrustGroupsUpdated', () => sync('onetrust'));
  // Never start unleash from here: it must be started by useFeatureFlags after
  // the user/country context is set, otherwise toggles are fetched (and cached)
  // for an anonymous user and flags disappear until the next login.
  unleash.on('ready', applyFlag);
  unleash.on('update', applyFlag);
  if (unleash.isReady()) applyFlag();
};
